import { ReactNode, createContext, useCallback, useContext, useMemo } from "react";

import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { PublicKey } from "@solana/web3.js";

import {
    useLazyTokenAccountsByOwnerQuery,
    useTokenAccountsByOwnerQuery,
    useLazyVerifySeekerGenesisTokenQuery,
    UserTokenAccount,
    SGTVerificationResult
} from "../reducers";
import { useLoopscaleWalletContext } from "../wallet";

export interface UserTokensState {
    tokens?: UserTokenAccount[];
    isLoading: boolean;
    refresh: () => Promise<void>;
    walletAddress?: string;
    error?: Error | null;
}

const UserTokensContext = createContext<UserTokensState | undefined>(undefined);

interface UserTokensProviderProps {
    children: ReactNode;
}

const FALLBACK_ERROR_MESSAGE = "Failed to load user tokens";

function normalizeQueryError(error?: FetchBaseQueryError | SerializedError): Error | null {
    if (!error) return null;

    if ("status" in error) {
        if ("error" in error) {
            const fetchError = error.error;
            if (typeof fetchError === "string" && fetchError.length > 0) {
                return new Error(fetchError);
            }
            if (fetchError && typeof fetchError === "object") {
                try {
                    return new Error(JSON.stringify(fetchError));
                } catch {
                    return new Error(FALLBACK_ERROR_MESSAGE);
                }
            }
        }

        if ("data" in error && error.data) {
            if (typeof error.data === "string") {
                return new Error(error.data);
            }
            try {
                return new Error(JSON.stringify(error.data));
            } catch {
                return new Error(FALLBACK_ERROR_MESSAGE);
            }
        }

        return new Error(FALLBACK_ERROR_MESSAGE);
    }

    if (error.message) {
        return new Error(error.message);
    }

    return new Error(FALLBACK_ERROR_MESSAGE);
}

export const UserTokensProvider = ({ children }: UserTokensProviderProps) => {
    const {
        data: { userWalletAddress: normalizedWalletAddress, userPublicKey }
    } = useLoopscaleWalletContext();

    const hasProvidedWalletAddress = normalizedWalletAddress !== undefined;

    const { providedPublicKey, providedPublicKeyError } = useMemo(() => {
        if (!normalizedWalletAddress) {
            return { providedPublicKey: null as PublicKey | null, providedPublicKeyError: null as Error | null };
        }

        try {
            return {
                providedPublicKey: new PublicKey(normalizedWalletAddress),
                providedPublicKeyError: null
            };
        } catch (error: unknown) {
            const normalizedError = error instanceof Error ? error : new Error(String(error));
            return {
                providedPublicKey: null,
                providedPublicKeyError: normalizedError
            };
        }
    }, [normalizedWalletAddress]);

    const walletPublicKey = providedPublicKey ?? userPublicKey ?? null;
    const walletAddress = walletPublicKey?.toBase58();

    const skipTokenQuery = !walletAddress;

    const {
        data: tokenAccounts,
        error: queryError,
        isFetching,
        isLoading: isInitialLoading
    } = useTokenAccountsByOwnerQuery(walletAddress ?? "", { skip: skipTokenQuery });

    const [triggerTokenAccountsQuery] = useLazyTokenAccountsByOwnerQuery();

    const refresh = useCallback(async () => {
        if (skipTokenQuery || !walletAddress) return;

        try {
            await triggerTokenAccountsQuery(walletAddress, false).unwrap();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to refresh token accounts", error);
        }
    }, [skipTokenQuery, triggerTokenAccountsQuery, walletAddress]);

    const queryDerivedError = useMemo(() => normalizeQueryError(queryError), [queryError]);

    const error = useMemo(() => {
        if (!walletAddress) {
            return hasProvidedWalletAddress ? providedPublicKeyError ?? null : null;
        }

        return queryDerivedError;
    }, [hasProvidedWalletAddress, providedPublicKeyError, queryDerivedError, walletAddress]);

    const tokens = walletAddress ? tokenAccounts : undefined;
    const isLoading = Boolean(walletAddress) && (isInitialLoading || isFetching);

    const contextValue = useMemo<UserTokensState>(
        () => ({
            tokens,
            isLoading,
            refresh,
            walletAddress,
            error
        }),
        [error, isLoading, refresh, tokens, walletAddress]
    );

    return <UserTokensContext.Provider value={contextValue}>{children}</UserTokensContext.Provider>;
};

export const useUserTokens = (): UserTokensState => {
    const context = useContext(UserTokensContext);
    if (!context) {
        throw new Error("useUserTokens must be used within a UserTokensProvider");
    }

    return context;
};

export interface SeekerGenesisTokenState {
    /** Whether verification is in progress */
    isVerifying: boolean;
    /** Trigger verification for potential SGT tokens */
    verify: () => Promise<SGTVerificationResult | null>;
}

/**
 * Hook to find and verify a Seeker Genesis Token (SGT) in the user's wallet.
 *
 * The SGT is a Token-2022 NFT that represents a verified owner of a Seeker device.
 * This hook:
 * 1. Finds potential SGT candidates (decimals: 0, amount: 1)
 * 2. Verifies the token has correct mint authority, metadata pointer, and group member
 *
 * Note: The backend handles verification that the SGT was originally minted to the wallet.
 *
 * @see https://docs.solanamobile.com/marketing/engaging-seeker-users#transferability
 */
export const useSeekerGenesisToken = (): SeekerGenesisTokenState => {
    const { tokens } = useUserTokens();
    const [triggerVerify, { isFetching }] = useLazyVerifySeekerGenesisTokenQuery();

    // Find potential SGT candidates (NFTs with decimals: 0 and amount: 1)
    const potentialSGTs = useMemo(() => {
        if (!tokens) return [];

        return tokens.filter((token) => token.tokenAmount.decimals === 0 && token.tokenAmount.amount === "1");
    }, [tokens]);

    const verify = useCallback(async (): Promise<SGTVerificationResult | null> => {
        if (potentialSGTs.length === 0) {
            return null;
        }

        // Check each potential SGT candidate and return the first valid one
        for (const token of potentialSGTs) {
            const result = await triggerVerify({
                mintAddress: token.mint,
                tokenAccountAddress: token.address
            }).unwrap();

            if (result.isValid) {
                return result;
            }
        }

        return null;
    }, [potentialSGTs, triggerVerify]);

    return {
        isVerifying: isFetching,
        verify
    };
};
